import api from '../../../shared/utils/api';

/** Outreach channel a cadence step can drive. */
export type CadenceChannel =
  | 'linkedin'
  | 'linkedin_conexao'
  | 'email'
  | 'telefone'
  | 'whatsapp'
  | 'reuniao'
  | 'nota';

/** Pipeline stage a cadence belongs to. Mirrors the backend CADENCE_STAGES
 *  vocabulary: prospecção → reunião → proposta → negociação → contrato →
 *  fechamento. */
export type CadenceStage =
  | 'prospeccao'
  | 'reuniao'
  | 'proposta'
  | 'negociacao'
  | 'contrato'
  | 'fechamento';

export type CadenceStepStatus = 'pending' | 'done' | 'skipped';

/** Cadence health signal (Phases 1+2, server-computed). */
export type CadenceTemperature = 'ok' | 'morno' | 'ruim';

/**
 * Provenance of a cadence (Onda 11, server-derived at read time):
 * `autopilot` (gerada pelo Autopilot do deal), `marketing` (régua de
 * marketing) ou `manual` ("Iniciar cadência" / funil completo / criação
 * avulsa). Mirrors the sales-api `compute_cadence_origin` vocabulary.
 */
export type CadenceOrigin = 'autopilot' | 'marketing' | 'manual';

export interface CadenceStep {
  id: string;
  cadenceId: string;
  orderIndex: number;
  channel: CadenceChannel;
  dayOffset: number;
  title: string;
  detail?: string | null;
  status: CadenceStepStatus;
  doneAt?: string | null;
  /**
   * Absolute due date (ISO datetime), server-computed in Phases 1+2. Prefer
   * this over `createdAt + dayOffset`. May be null on legacy rows during the
   * soak window — callers must fall back to the day-offset computation.
   */
  scheduledAt?: string | null;
  activityId?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Cadence {
  id: string;
  tenantId: string;
  contactId: string;
  // A cadence is always anchored to a deal/prospect.
  dealId: string;
  stage: CadenceStage;
  name: string;
  isPinned: boolean;
  /**
   * Active flag (Phase 7b). When false, the cadence is "paused" — it stays in
   * the DB for history but is filtered out of the default UI view and skipped
   * by the aggregate next-step computation. The BFF ships this as
   * `isActive` (camelCased from backend `is_active`). May be absent on legacy
   * rows during the soak window between the Phase 7a backend deploy and the
   * frontend deploy — treat undefined as `true` (active) for backward compat.
   */
  isActive?: boolean;
  /**
   * Provenance of the cadence (Onda 11), computed server-side at read time.
   * May be absent during the backend deploy soak — callers must fall back to
   * the client-side classification (see `cadenceOrigin` in CadenceManager,
   * which mirrors the same criteria).
   */
  origin?: CadenceOrigin;
  /** Health signal computed server-side (Phases 1+2). Absent on legacy rows. */
  temperature?: CadenceTemperature | null;
  createdAt: string;
  updatedAt: string;
  steps: CadenceStep[];
}

/**
 * One row of the aggregated "Próximos passos" list shown on the Meu Dia page.
 * Each item is the next pending step of one of the user's active cadences,
 * with the deal/contact context flattened in. Mirrors the BFF response shape
 * (camelCased from the sales-api `CadenceNextStepItem`).
 */
export interface CadenceNextStep {
  cadenceId: string;
  stepId: string;
  dealId: string;
  contactId?: string | null;
  contactName?: string | null;
  companyName?: string | null;
  stepTitle: string;
  channel: CadenceChannel;
  stage: CadenceStage;
  scheduledAt?: string | null;
  temperature?: CadenceTemperature | null;
  isOverdue: boolean;
  /** Real LinkedIn URL of the contact (synthetic handles filtered server-side). */
  linkedinUrl?: string | null;
  /** True when this step is an automatic engagement follow-up (click → task). */
  isAutoFollowUp?: boolean;
  /** Human-readable WHY of the auto follow-up ("Follow-up automático: …"). */
  followUpReason?: string | null;
}

export interface CadenceNextStepsResponse {
  items: CadenceNextStep[];
  total: number;
}

/**
 * One row of the "Iniciar cadência" Meu Dia card — a deal that has at least one
 * revealed (unlocked) contact but no cadence yet. `firstContactId` is the
 * legacy `contacts.id`, ready to hand straight to `createFullFunnel`.
 */
export interface DealWithoutCadence {
  dealId: string;
  companyName: string;
  stage: string;
  revealedContactCount: number;
  firstContactName?: string | null;
  firstContactId?: string | null;
}

export interface DealsWithoutCadenceResponse {
  items: DealWithoutCadence[];
  total: number;
}

export interface CadenceStepInput {
  channel: CadenceChannel;
  title: string;
  detail?: string;
  dayOffset?: number;
  orderIndex?: number;
}

export interface CreateCadencePayload {
  contactId: string;
  dealId: string;
  stage?: CadenceStage;
  name: string;
  isPinned?: boolean;
  steps?: CadenceStepInput[];
}

export interface UpdateCadencePayload {
  name?: string;
  stage?: CadenceStage;
  isPinned?: boolean;
}

export interface UpdateStepPayload {
  title?: string;
  detail?: string;
  channel?: CadenceChannel;
  dayOffset?: number;
  orderIndex?: number;
  status?: CadenceStepStatus;
  activityId?: string;
  /** Free-text note logged to the activity timeline when closing a step. */
  observation?: string;
  /** Closing date (ISO datetime) recorded when marking a step done. */
  completedAt?: string;
  /** "Adiar" — reschedule the step (ISO datetime) without completing it. */
  scheduledAt?: string;
  /** Queue-adoption telemetry: where the completion happened. */
  completedFrom?: 'meu_dia' | 'deal' | 'regua';
}

/** Result of the nurture follow-up create/dismiss endpoints. */
export interface NurtureFollowUpResult {
  stepId: string;
  cadenceId: string;
  status: CadenceStepStatus;
  created: boolean;
}

/**
 * One follow-up step in the régua-results lens (bulk status, R2.1).
 * `contactCoreId` rides along because the régua's engagement rows arrive in
 * mixed id-spaces — match by EITHER id.
 */
export interface NurtureFollowUpStatusItem {
  stepId: string;
  cadenceId: string;
  dealId: string;
  contactId: string;
  contactCoreId?: string | null;
  status: CadenceStepStatus;
  scheduledAt?: string | null;
  doneAt?: string | null;
}

export interface NurtureFollowUpStatusResponse {
  items: NurtureFollowUpStatusItem[];
  total: number;
}

// BFF mounts the sales CRUD router at /api/v1 (no `/sales` segment), so cadences
// live at /api/v1/activity-cadences — same convention as /pipeline, /contacts,
// /briefings. The axios client already prefixes baseURL `/api/v1`.
const BASE = '/activity-cadences';

export const cadencesApi = {
  listByContact: async (contactId: string): Promise<Cadence[]> => {
    const { data } = await api.get<Cadence[]>(`${BASE}/by-contact/${contactId}`);
    return data;
  },

  listByDeal: async (dealId: string): Promise<Cadence[]> => {
    const { data } = await api.get<Cadence[]>(`${BASE}/by-deal/${dealId}`);
    return data;
  },

  /**
   * Aggregated "Próximos passos" for the current user — the next pending step
   * of each of their active cadences, overdue first. Owner-scoping is enforced
   * server-side from the JWT (the BFF forwards X-User-Id). Backs the Meu Dia
   * "Próximos passos" card.
   */
  listNextSteps: async (limit?: number): Promise<CadenceNextStepsResponse> => {
    const { data } = await api.get<CadenceNextStepsResponse>(`${BASE}/next-steps`, {
      params: limit !== undefined ? { limit } : undefined,
    });
    return data;
  },

  /**
   * Deals owned by the current user that have a revealed contact but no cadence
   * yet — backs the Meu Dia "Iniciar cadência" card. Owner-scoping is enforced
   * server-side from the JWT (the BFF forwards X-User-Id).
   */
  listDealsWithoutCadence: async (limit = 20): Promise<DealsWithoutCadenceResponse> => {
    const { data } = await api.get<DealsWithoutCadenceResponse>(`${BASE}/deals-without-cadence`, {
      params: { limit },
    });
    return data;
  },

  get: async (cadenceId: string): Promise<Cadence> => {
    const { data } = await api.get<Cadence>(`${BASE}/${cadenceId}`);
    return data;
  },

  create: async (payload: CreateCadencePayload): Promise<Cadence> => {
    const { data } = await api.post<Cadence>(BASE, payload);
    return data;
  },

  /**
   * Create the whole funnel (Prospecção → Reunião → Proposta → Fechamento) for
   * a contact in one call, with AI-personalised step text (server-side).
   * Idempotent per (contact, deal, stage). Returns every active cadence for the
   * contact, so the UI can render them as side-by-side pipeline columns.
   */
  createFullFunnel: async (contactId: string, dealId: string): Promise<Cadence[]> => {
    const { data } = await api.post<Cadence[]>(`${BASE}/full-funnel`, { contactId, dealId });
    return data;
  },

  update: async (cadenceId: string, payload: UpdateCadencePayload): Promise<Cadence> => {
    const { data } = await api.patch<Cadence>(`${BASE}/${cadenceId}`, payload);
    return data;
  },

  /**
   * Toggle the cadence active flag (Phase 7b). Routes to the dedicated
   * `/active` endpoint added by the BFF in Phase 7a — body is camelCased
   * `{ isActive }` to match the rest of the cadence Zod schemas (the BFF
   * proxies to the backend, which accepts the same shape; the backend itself
   * persists `is_active` snake_case). Returns the patched cadence.
   */
  setCadenceActive: async (cadenceId: string, isActive: boolean): Promise<Cadence> => {
    const { data } = await api.patch<Cadence>(`${BASE}/${cadenceId}/active`, { isActive });
    return data;
  },

  remove: async (cadenceId: string): Promise<void> => {
    await api.delete(`${BASE}/${cadenceId}`);
  },

  addStep: async (cadenceId: string, payload: CadenceStepInput): Promise<CadenceStep> => {
    const { data } = await api.post<CadenceStep>(`${BASE}/${cadenceId}/steps`, payload);
    return data;
  },

  updateStep: async (
    cadenceId: string,
    stepId: string,
    payload: UpdateStepPayload,
  ): Promise<CadenceStep> => {
    const { data } = await api.patch<CadenceStep>(`${BASE}/${cadenceId}/steps/${stepId}`, payload);
    return data;
  },

  removeStep: async (cadenceId: string, stepId: string): Promise<void> => {
    await api.delete(`${BASE}/${cadenceId}/steps/${stepId}`);
  },

  /**
   * Bulk follow-up status for one régua (the régua-results lens): one row
   * per engagement follow-up step the sequence contributed a signal to.
   */
  listNurtureFollowUps: async (sequenceId: string): Promise<NurtureFollowUpStatusResponse> => {
    const { data } = await api.get<NurtureFollowUpStatusResponse>(`${BASE}/nurture-follow-ups`, {
      params: { sequenceId },
    });
    return data;
  },

  /**
   * Mint (or refresh) the pending LinkedIn follow-up of a (deal, contact) —
   * the manual counterpart of the automatic click → task recorder, used by
   * the régua results screen for opened-only contacts. Idempotent: an
   * existing pending follow-up absorbs the call (`created: false`).
   */
  createNurtureFollowUp: async (payload: {
    dealId: string;
    contactId: string;
    sequenceId?: string;
    sequenceName?: string;
    trigger?: 'clicked' | 'opened' | 'manual';
  }): Promise<NurtureFollowUpResult> => {
    const { data } = await api.post<NurtureFollowUpResult>(`${BASE}/nurture-follow-up`, payload);
    return data;
  },

  /** Dismiss (skip) the pending follow-up of a (deal, contact). 404 = nothing pending. */
  dismissNurtureFollowUp: async (payload: {
    dealId: string;
    contactId: string;
  }): Promise<NurtureFollowUpResult> => {
    const { data } = await api.post<NurtureFollowUpResult>(
      `${BASE}/nurture-follow-up/dismiss`,
      payload,
    );
    return data;
  },
};
